/**
 * 合併已存 columnOrder 與目前 leaf columns。
 * - select：固定最左
 * - actions：固定 select 之後（列表「操作」／編輯欄維持最前）
 * - meta.enableColumnOrder === false：不可拖，相對位置跟 columnDef 中段
 * - 可排欄：saved 仍存在者依序保留；新增接在可排區尾端；已刪除 id 丟掉
 */

/**
 * @param {object} column — TanStack Column 或 columnDef（需有 id / columnDef.meta）
 * @returns {boolean}
 */
export function isColumnOrderLocked(column) {
  const id = column?.id ?? column?.columnDef?.id;
  if (id === 'select' || id === 'actions') {
    return true;
  }
  const meta = column?.columnDef?.meta ?? column?.meta;
  return meta?.enableColumnOrder === false;
}

/**
 * @param {Array<object>} leafColumns
 * @returns {string[]}
 */
export function getLeafColumnIds(leafColumns = []) {
  return leafColumns
    .map((column) => column?.id ?? column?.columnDef?.id ?? column?.accessorKey)
    .filter((id) => typeof id === 'string' && id.length > 0);
}

/**
 * @param {string[]|null|undefined} savedOrder
 * @param {Array<object>} leafColumns
 * @returns {string[]}
 */
export function mergeColumnOrder(savedOrder, leafColumns = []) {
  const defaultIds = getLeafColumnIds(leafColumns);
  if (defaultIds.length === 0) {
    return [];
  }

  const columnById = new Map();
  leafColumns.forEach((column) => {
    const id = column?.id ?? column?.columnDef?.id ?? column?.accessorKey;
    if (id) {
      columnById.set(String(id), column);
    }
  });

  const selectIds = defaultIds.filter((id) => id === 'select');
  const actionIds = defaultIds.filter((id) => id === 'actions');
  const middleDefault = defaultIds.filter((id) => id !== 'select' && id !== 'actions');

  const middleLockedSet = new Set(
    middleDefault.filter((id) => {
      const column = columnById.get(id);
      const meta = column?.columnDef?.meta ?? column?.meta;
      return meta?.enableColumnOrder === false;
    }),
  );

  const orderableDefault = middleDefault.filter((id) => !middleLockedSet.has(id));
  const saved = Array.isArray(savedOrder)
    ? savedOrder.filter((id) => orderableDefault.includes(id))
    : [];
  const missing = orderableDefault.filter((id) => !saved.includes(id));
  const orderableMerged = [...saved, ...missing];

  let orderableIndex = 0;
  const middle = middleDefault.map((id) => {
    if (middleLockedSet.has(id)) {
      return id;
    }
    const nextId = orderableMerged[orderableIndex];
    orderableIndex += 1;
    return nextId;
  });

  return [...selectIds, ...actionIds, ...middle.filter(Boolean)];
}

/**
 * @param {Array<object>} leafColumns
 * @returns {string[]}
 */
export function getOrderableColumnIds(leafColumns = []) {
  return getLeafColumnIds(leafColumns).filter((id) => {
    const column = leafColumns.find(
      (item) => (item?.id ?? item?.columnDef?.id ?? item?.accessorKey) === id,
    );
    return column ? !isColumnOrderLocked(column) : false;
  });
}
